import { FC } from 'react';

import DisclaimerText from 'components/DisclaimerText';
import HorizontalBarChart from 'components/HorizontalBarChart';

import Badge from 'primitives/Badge';

import industryToReadable from 'utils/enums/industry-to-readable';
import stageToReadable from 'utils/enums/stage-to-readable';
import { formatCurrencyToShort } from 'utils/format-helper';

import { useSyndicateAnalyticsData } from '.';

const YearWisePerformance: FC = () => {
  const { analytics } = useSyndicateAnalyticsData();

  return (
    <div className="space-y-6 mb-12">
      <DisclaimerText
        message={
          <>
            <p>
              Year wise and scheme wise figures are computed independently and do not sum or average
              to syndicate level totals.
            </p>
            <p>
              XIRR is shown as nil where it cannot be computed reliably - typically when there is no
              realised cashflows or where insufficient time has elapsed since investment.
            </p>
          </>
        }
      />
      {analytics.yearWisePerformance.map(year => (
        <section
          key={year.year}
          className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">{year.year}</h2>
              <p className="mt-1 text-sm text-gray-500">
                {year.numberOfDeals} deals across {year.numberOfCompanies} companies
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div className="rounded-xl bg-gray-50 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">XIRR</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {year.xirr !== null && year.xirr !== undefined ? `${year.xirr.toFixed(2)}%` : '-'}
                </p>
              </div>
              <div className="rounded-xl bg-gray-50 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">DPI</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">{year.dpi.toFixed(2)}x</p>
              </div>
              <div className="rounded-xl bg-gray-50 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">TVPI</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">{year.tvpi.toFixed(2)}x</p>
              </div>
              <div className="rounded-xl bg-gray-50 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Invested</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {formatCurrencyToShort(year.investedCapital)}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-gray-100 p-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Stage Breakdown
              </h3>
              <div className="mt-4 space-y-4">
                {year.stageBreakdown.map(item => {
                  const stage = stageToReadable(item.stage as any);

                  return (
                    <div key={`${year.year}-stage-${item.stage}`}>
                      <div className="mb-2 flex items-center justify-between gap-4">
                        <Badge label={stage.label} color={stage.color} />
                        <span className="text-sm font-medium text-gray-700">
                          {item.percentage.toFixed(2)}%
                        </span>
                      </div>
                      <HorizontalBarChart percentage={item.percentage} />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-xl border border-gray-100 p-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Sector Breakdown
              </h3>
              <div className="mt-4 space-y-4">
                {year.sectorBreakdown.map(item => {
                  const industry = industryToReadable(item.industry as any);

                  return (
                    <div key={`${year.year}-industry-${item.industry}`}>
                      <div className="mb-2 flex items-center justify-between gap-4">
                        <Badge label={industry.label} color={industry.color} />
                        <span className="text-sm font-medium text-gray-700">
                          {item.percentage.toFixed(2)}%
                        </span>
                      </div>
                      <HorizontalBarChart percentage={item.percentage} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-gray-100 p-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              Companies Invested
            </h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {year.companies.map(company => (
                <Badge key={company.companyId} label={company.companyName} color="indigo" />
              ))}
            </div>
          </div>
        </section>
      ))}
    </div>
  );
};

export default YearWisePerformance;
